import React, { useEffect, useMemo, useRef, useState } from "react";

import CountryFlag from "../../components/CountryFlag";
import { toDateInputValue } from "../../utils/orderDates";
import styles from "./OrdersTable.module.css";

/**
 * Shared order field set (same as inline table editor).
 * @param {{ order: object, countries?: object[], onChange: (field: string, value: string) => void }} props
 */
function CountryCombobox({ label, value, countries, onChange }) {
  const rootRef = useRef(null);
  const selectedCountry = countries.find((country) => String(country.id) === String(value ?? ""));
  const selectedLabel = selectedCountry
    ? `${selectedCountry.name} (${selectedCountry.code})`
    : "";
  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick, true);
    };
  }, [open]);

  const filteredCountries = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return countries;
    }
    return countries.filter((country) => {
      const name = country.name.toLowerCase();
      const code = country.code.toLowerCase();
      return name.includes(needle) || code.includes(needle);
    });
  }, [countries, query]);

  const handleQueryChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setOpen(true);
    if (!nextQuery.trim()) {
      onChange("");
    }
  };

  const selectCountry = (country) => {
    onChange(String(country.id));
    setQuery(`${country.name} (${country.code})`);
    setOpen(false);
  };

  return (
    <div className={styles.formGroup} ref={rootRef}>
      <label className={styles.label}>{label}</label>
      <div className={styles.countryCombobox}>
        <div className={styles.countrySearchRow}>
          {selectedCountry && (
            <CountryFlag
              code={selectedCountry.code}
              label={`${selectedCountry.name} flag`}
              className={styles.flagIcon}
            />
          )}
          <input
            type="text"
            className={styles.countrySearchInput}
            value={open ? query : selectedLabel}
            onChange={handleQueryChange}
            onFocus={() => {
              setQuery(selectedLabel);
              setOpen(true);
            }}
            placeholder={`Search ${label.toLowerCase()} by name or code`}
            autoComplete="off"
          />
          {selectedCountry && (
            <button
              type="button"
              className={styles.countryClearButton}
              onClick={() => {
                onChange("");
                setQuery("");
                setOpen(true);
              }}
              aria-label={`Clear ${label.toLowerCase()}`}
            >
              x
            </button>
          )}
        </div>
        {open && (
          <div className={styles.countryDropdown}>
            {filteredCountries.length === 0 ? (
              <div className={styles.countryOptionEmpty}>No countries found</div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  type="button"
                  key={country.id}
                  className={styles.countryOption}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCountry(country)}
                >
                  <CountryFlag
                    code={country.code}
                    label={`${country.name} flag`}
                    className={styles.flagIcon}
                  />
                  <span>{country.name}</span>
                  <span className={styles.countryCode}>{country.code}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderFormFields({ order, countries = [], onChange }) {
  return (
    <div className={styles.formGrid}>
      <div className={styles.formGroup}>
        <label className={styles.label}>Internal # *</label>
        <input
          type="text"
          className={styles.input}
          value={order.internalNo ?? ""}
          onChange={(e) => onChange("internalNo", e.target.value)}
          placeholder="Enter internal number"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Component Name</label>
        <input
          type="text"
          className={styles.input}
          value={order.componentName || ""}
          onChange={(e) => onChange("componentName", e.target.value)}
          placeholder="Enter component name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Part Number *</label>
        <input
          type="text"
          className={styles.input}
          value={order.partNumber ?? ""}
          onChange={(e) => onChange("partNumber", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Serial Number *</label>
        <input
          type="text"
          className={styles.input}
          value={order.serialNumber ?? ""}
          onChange={(e) => onChange("serialNumber", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Customer *</label>
        <input
          type="text"
          className={styles.input}
          value={order.customerName ?? ""}
          onChange={(e) => onChange("customerName", e.target.value)}
          placeholder="Enter customer name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Status</label>
        <select
          className={styles.select}
          value={order.status || "ordered"}
          onChange={(e) => onChange("status", e.target.value)}
        >
          <option value="ordered">Ordered</option>
          <option value="progress">Progress</option>
          <option value="released">Released</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <CountryCombobox
        label="Origin"
        value={order.originCountryId}
        countries={countries}
        onChange={(value) => onChange("originCountryId", value)}
      />

      <CountryCombobox
        label="Destination"
        value={order.destinationCountryId}
        countries={countries}
        onChange={(value) => onChange("destinationCountryId", value)}
      />

      <div className={styles.formGroup}>
        <label className={styles.label}>Date In</label>
        <input
          type="date"
          className={styles.input}
          value={toDateInputValue(order.dateIn)}
          onChange={(e) => onChange("dateIn", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Work started</label>
        <input
          type="date"
          className={styles.input}
          value={toDateInputValue(order.workStarted)}
          onChange={(e) => onChange("workStarted", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Work completed</label>
        <input
          type="date"
          className={styles.input}
          value={toDateInputValue(order.workCompleted)}
          onChange={(e) => onChange("workCompleted", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Date Out</label>
        <input
          type="date"
          className={styles.input}
          value={toDateInputValue(order.dateOut)}
          onChange={(e) => onChange("dateOut", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Reason</label>
        <textarea
          className={styles.textarea}
          value={order.reason ?? ""}
          onChange={(e) => onChange("reason", e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Remarks</label>
        <textarea
          className={styles.textarea}
          value={order.remarks ?? ""}
          onChange={(e) => onChange("remarks", e.target.value)}
        />
      </div>
    </div>
  );
}

export default OrderFormFields;
